/**
 * WarehouseSyncButton — the icon-only "Refresh from RudderStack" action on the Warehouses settings
 * page, the size of the list toolbar's Filter and Sort buttons. It pulls newly-added RudderStack
 * destinations and accounts in. Admin-gated: non-admins see it disabled with an explanatory tooltip.
 */
import { RefreshCw } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/_shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';

const LABEL = 'Refresh from RudderStack';

interface WarehouseSyncButtonProps {
  /** ACT2-1276: why the RudderStack permission blocks the sync, or null. */
  blockedReason: string | null;
  isSyncing: boolean;
  onSync: () => void;
}

export function WarehouseSyncButton({ blockedReason, isSyncing, onSync }: WarehouseSyncButtonProps): React.JSX.Element {
  const button = (
    <Button
      type="button"
      variant="secondary"
      size="md"
      iconOnly
      aria-label={LABEL}
      disabled={blockedReason !== null || isSyncing}
      loading={isSyncing}
      onClick={onSync}
      Icon={<RefreshCw />}
    />
  );

  // The label is not on screen, so the tooltip always explains the button; non-admins get the gate
  // reason instead. The disabled non-admin button can't receive pointer events, so it is wrapped in a
  // focusable span.
  return (
    <Tooltip>
      <TooltipTrigger asChild>{blockedReason === null ? button : <span tabIndex={0}>{button}</span>}</TooltipTrigger>
      <TooltipContent>{blockedReason ?? LABEL}</TooltipContent>
    </Tooltip>
  );
}
